import { useState } from 'react';
import Locator from '../../../../shared/Locator';
import { BINARY_DECOMPOSITION, DISABLE_SBOM_ORIGINALS, HYBRID_PROJECTS } from '../../../../shared/featureFlags/flagNames';
import { FileType } from '../../../../shared/fileUpload';
import { isFeatureEnabled } from '../../../helpers/features';
import { nuc } from '../../../utils';
import { Icon, icons } from '../../generic';
import IconText from '../../generic/Icon/IconText';
import styles from './DisplayProjectNav.module.less';
import ModalUploadRevision from './ModalUploadRevision';

type Props = {
  locator: string;
};

/**
 * Get the appropriate API endpoint for downloading the original file
 */
const getDownloadEndpoint = (locator: string): string => {
  return `/api/revisions/${nuc(locator)}/original-sbom`;
};

/**
 * Button text component for the download button
 */
const DownloadButtonText = () => {
  const isHybridProjectsEnabled = isFeatureEnabled(HYBRID_PROJECTS);

  if (isHybridProjectsEnabled) {
    return (
      <>
        <Icon icon={icons.down} />
        <span>Download Original</span>
      </>
    );
  }

  return <IconText icon={icons.download} text="Download Original" marginAmount="4px" />;
};

/**
 * Button text component for the upload button
 */
const UploadButtonText = ({ fileType }: { fileType: FileType.SBOM | FileType.Binary }) => {
  const isHybridProjectsEnabled = isFeatureEnabled(HYBRID_PROJECTS);
  const fileTypeName = fileType === FileType.SBOM ? 'SBOM' : 'Binary';

  if (isHybridProjectsEnabled) {
    return (
      <>
        <Icon icon={icons.up} />
        <span>Upload New {fileTypeName}</span>
      </>
    );
  }

  return (
    <>
      <Icon icon={icons.upload} />
      <span className="font-family-monospace">Upload New {fileTypeName}</span>
    </>
  );
};

/**
 * Component that provides UI affordances for downloading and uploading revisions
 */
const RevisionAffordances = ({ locator }: Props) => {
  const [shouldShowModal, setShouldShowModal] = useState(false);

  // Determine if we should show this component based on project type
  const { fetcher } = Locator.parse(locator);
  const isBinaryProject = fetcher === 'binary';
  const fileType = fetcher === 'sbom' ? FileType.SBOM : FileType.Binary;
  const isRelevantProject = fetcher === 'sbom' || (isBinaryProject && isFeatureEnabled(BINARY_DECOMPOSITION));

  // Only show for relevant project types and when not disabled by feature flag
  const shouldShow = !isFeatureEnabled(DISABLE_SBOM_ORIGINALS) && isRelevantProject;

  const isHybridProjectsEnabled = isFeatureEnabled(HYBRID_PROJECTS);

  // Determine CSS class names based on the file type
  const downloadStyles = isHybridProjectsEnabled
    ? styles.hybridProjectSBOMDownload
    : styles.sbomDownload;

  const uploadButtonStyles = isHybridProjectsEnabled
    ? styles.hybridProjectSBOMUploadButton
    : styles.sbomUploadButton;

  const containerStyles = isHybridProjectsEnabled
    ? `${styles.hybridProjectSBOMAffordances} nav-project-description`
    : styles.sbomAffordances;

  if (!shouldShow) {
    return null;
  }

  return (
    <div className={containerStyles}>
      {!isBinaryProject && (
        <p className={`animated fadeInDown fast ${styles.sbomControl}`}>
          <a className={downloadStyles} href={getDownloadEndpoint(locator)} download>
            <DownloadButtonText />
          </a>
        </p>
      )}
      <p className={`animated fadeInDown fast ${styles.sbomControl}`}>
        <button
          type="button"
          className={uploadButtonStyles}
          onClick={() => {
            setShouldShowModal(true);
          }}
        >
          <UploadButtonText fileType={fileType} />
        </button>
      </p>
      <ModalUploadRevision fileType={fileType} onHide={() => setShouldShowModal(false)} shouldShow={shouldShowModal} />
    </div>
  );
};

export default RevisionAffordances;
